import { ArrayUtil } from '@athena-lib/ProductWizard/util/ArrayUtil';
import { productWizardConsole } from '@athena-lib/ProductWizard/util/productWizardLog';
import type { ProductAttrMapInterface } from '../model/attr/ProductAttrMapInterface';
import type { ProductAttrInterface } from '../model/attr/ProductAttrInterface';
import type { ProductAttrFilterInterface } from '../model/attr/ProductAttrFilterInterface';
import type { ProductAttrFilterCallbackInterface } from '../model/attr/ProductAttrFilterCallbackInterface';

export class ProductAttrConditionEvaluator {
  public attributes: ProductAttrMapInterface = {};
  public regex: RegExp = /[$][{].*?[}]/g;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  protected ignoredConstraints: any = {};
  protected logEnabled: boolean = false;
  public debugEnabled: boolean = false;
  public debugList: string[] = [];
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  protected debugConstraints: any = {};

  public reset(attributes: ProductAttrMapInterface): void {
    this.attributes = attributes;
  }

  public clearDebug(): void {
    this.debugList = [];
    this.debugConstraints = {};
  }

  protected debug(result: string): void {
    this.debugList.push(result);
  }

  protected debugConstraint(
    attributeName: string,
    attributeOption: PropertyKey,
    reasonOfConstraint: string,
  ): void {
    if (this.debugEnabled) {
      if (!Object.prototype.hasOwnProperty.call(this.debugConstraints, attributeName)) {
        this.debugConstraints[attributeName] = {};
      }
      if (
        !Object.prototype.hasOwnProperty.call(this.debugConstraints[attributeName], attributeOption)
      ) {
        this.debugConstraints[attributeName][attributeOption] = [];
      }

      this.debugConstraints[attributeName][attributeOption].push(reasonOfConstraint);
    }
  }

  public getDebugConstraints(attributeName: string, attributeOption: PropertyKey): string[] | null {
    if (Object.prototype.hasOwnProperty.call(this.debugConstraints, attributeName)) {
      if (
        Object.prototype.hasOwnProperty.call(this.debugConstraints[attributeName], attributeOption)
      ) {
        return this.debugConstraints[attributeName][attributeOption];
      }
    }

    return null;
  }

  protected log(...args: unknown[]): void {
    if (this.logEnabled) {
      productWizardConsole.log(...args);
    }
  }

  public ignoreConstraintsFromTo(fromAttribute: string, toAttribute: string): void {
    if (!Object.prototype.hasOwnProperty.call(this.ignoredConstraints, fromAttribute)) {
      this.ignoredConstraints[fromAttribute] = [];
    }

    this.ignoredConstraints[fromAttribute].push(toAttribute);
  }

  public shouldIgnoreConstraints(fromAttribute: string, toAttribute: string): boolean {
    if (
      Object.prototype.hasOwnProperty.call(this.ignoredConstraints, fromAttribute) ||
      Object.prototype.hasOwnProperty.call(this.ignoredConstraints, '*')
    ) {
      return (
        this.ignoredConstraints[fromAttribute].indexOf(toAttribute) >= 0 ||
        this.ignoredConstraints[fromAttribute].indexOf('*') >= 0
      );
    }

    return false;
  }

  public filter(
    productItemAsOneDimensionalObject: unknown,
    attrName: string,
    callback: ProductAttrFilterCallbackInterface | undefined | null,
  ): (string | number)[] {
    const attr = this.getAttribute(attrName);
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    let values: any = [];
    let filters: ProductAttrFilterInterface[] | null = null;
    if (attr) {
      if (attr.filters) {
        // 1. All filters that passes conditions
        filters = attr.filters.filter((attrFilter) => {
          return this.evalCondition(attrFilter.conditions, productItemAsOneDimensionalObject);
        });
        // 2. Sort them by complexity score, highest complexity score wins
        filters.sort((a, b) => {
          return b.conditionsComplexityScore - a.conditionsComplexityScore;
        });

        if (filters?.length) {
          switch (attr.filterMode) {
            case 'merge_all_winners':
              filters.forEach((filter) => {
                values = values.concat(filter.values);
              });

              // Unique values after merge
              values = [...Array.from(new Set(values))];

              break;
            case 'highest_score_wins':
            default:
              productWizardConsole.log('Winning filter for ', attrName, filters[0]);
              values = filters[0]?.values;

              break;
          }

          if (!attr.isDynamicValue) {
            // Removing invalid values
            values = values.filter((value: PropertyKey) => {
              return Object.prototype.hasOwnProperty.call(attr.valuesAndConstraints, value);
            });
          }
        }
      }
    }

    return callback ? callback(attrName, values, filters) : values;
  }

  /**
   * Will test against your constraints to see if you can make a change.
   *
   * @param productItemAsOneDimensionalObject
   * @param attrNameToBeChanged
   * @param attrValueToBeChanged
   */
  public canIChangeAttribute(
    productItemAsOneDimensionalObject: unknown,
    attrNameToBeChanged: string,
    attrValueToBeChanged: string | number,
  ): boolean {
    // First we test if any of the current item values has any objections
    // on changing this attr value
    //		productWizardLogger.log( "Testing", attrNameToBeChanged, attrValueToBeChanged );
    const okToChange: boolean = this.doesCurrentItemAllowToChange(
      productItemAsOneDimensionalObject,
      attrNameToBeChanged,
      attrValueToBeChanged,
    );
    if (!okToChange) {
      return false;
    }

    // ..if not we test the other way around:
    // if the new value has any objections on the current attribute values
    return this.doesAttrValueAllowToBeCombinedWith(
      productItemAsOneDimensionalObject,
      attrNameToBeChanged,
      attrValueToBeChanged,
    );
  }

  public doesAttrValueAllowToBeCombinedWith(
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    productItemAsOneDimensionalObject: any,
    attrNameToBeChanged: string,
    attrValueToBeChanged: string | number,
  ): boolean {
    const condition = this.getConstraintsFor(attrNameToBeChanged, attrValueToBeChanged);
    if (condition) {
      let result: boolean = true;
      const conditionedColumnNames = condition.match(this.regex);
      if (conditionedColumnNames) {
        conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
          const conditionedColumnName =
            this.extractConditionedColumnName(conditionColumnNameAsCode);
          if (conditionedColumnName && result) {
            result = this.evalConditionAgainstChangeRequest(
              condition,
              productItemAsOneDimensionalObject,
              conditionedColumnName,
              productItemAsOneDimensionalObject[conditionedColumnName],
            );
          }
        });

        if (!result && this.debugEnabled) {
          let debugMsg: string = 'Blocked by ';
          let debugValues: Array<string> = [];
          conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
            const conditionedColumnName =
              this.extractConditionedColumnName(conditionColumnNameAsCode);
            const conditionedColumnNiceName = conditionedColumnName?.replace(
              'item.attributes.',
              '',
            );
            if (conditionedColumnName) {
              debugValues.push(
                `'${conditionedColumnNiceName}' (${productItemAsOneDimensionalObject[conditionedColumnName]})`,
              );
            }
          });

          debugValues = ArrayUtil.unique(debugValues);

          if (debugValues.length > 1) {
            debugMsg += 'any of these: ';
          }

          debugMsg = debugMsg + debugValues.join(', ');
          this.debugConstraint(attrNameToBeChanged, attrValueToBeChanged, debugMsg);
          this.debug(debugMsg);
        }
      }

      return result;
    }

    return true;
  }

  public doesCurrentItemAllowToChange(
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    productItemAsOneDimensionalObject: any,
    attrNameToBeChanged: string,
    attrValueToBeChanged: string | number,
  ) {
    for (const columnName in productItemAsOneDimensionalObject) {
      const attrName =
        columnName.indexOf('item.attributes.') >= 0
          ? columnName.replace('item.attributes.', '')
          : false;

      if (attrName && attrName != attrNameToBeChanged) {
        if (!this.shouldIgnoreConstraints(attrName, attrNameToBeChanged)) {
          const attr = this.getAttribute(attrName);
          if (attr) {
            let allValues = productItemAsOneDimensionalObject[columnName];
            if (!Array.isArray(allValues)) {
              allValues = [allValues];
            }
            for (const attrValueIndex in allValues) {
              const attrValue = allValues[attrValueIndex];
              const condition = this.getConstraintsFor(attrName, attrValue);
              if (condition) {
                const result = this.evalConditionAgainstChangeRequest(
                  condition,
                  productItemAsOneDimensionalObject,
                  'item.attributes.' + attrNameToBeChanged,
                  attrValueToBeChanged,
                );
                if (!result) {
                  const debug = `'${attrValue} ${attrName}' doesn't allow '${attrNameToBeChanged}' to be changed to '${attrValueToBeChanged}'.`;
                  this.debugConstraint(
                    attrNameToBeChanged,
                    attrValueToBeChanged,
                    `Blocked by '${attrName}' (${attrValue})`,
                  );
                  this.log(debug, attr, condition);
                  this.debug(debug);
                  return result;
                } else {
                  this.log(`'${attr}:${attrValue}'`, true);
                }
              }
            }
          }
        }
      }
    }

    return true;
  }

  protected evalConditionAgainstChangeRequest(
    condition: string,
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    productItemAsOneDimensionalObject: any,
    columnNameToBeChanged: string,
    columnValueToBeChanged: string | number,
  ): boolean {
    let result = true;
    // Does this condition care about the attr that we're testing?
    //		productWizardLogger.log( "evalConditionAgainstChangeRequest", condition, columnNameToBeChanged, condition.indexOf( "${" + columnNameToBeChanged + "}" ) );
    if (condition.indexOf('${' + columnNameToBeChanged + '}') >= 0) {
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      const replacedValues: any = {};
      const conditionedColumnNames = condition.match(this.regex);
      if (conditionedColumnNames) {
        conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
          const columnName = this.extractConditionedColumnName(conditionColumnNameAsCode);
          if (columnName == columnNameToBeChanged) {
            condition = condition.replace(conditionColumnNameAsCode, 'columnValueToBeChanged');
            replacedValues['columnValueToBeChanged'] = columnValueToBeChanged;
          } else {
            condition = condition.replace(
              conditionColumnNameAsCode,
              "productItemAsOneDimensionalObject['" + columnName + "']",
            );
            replacedValues['productItemAsOneDimensionalObject[' + columnName + ']'] =
              productItemAsOneDimensionalObject[<string>columnName];
          }
        });
      }
      try {
        this.log('testConditionAgainstChangeRequest:', condition, replacedValues);
        result = eval(condition);
      } catch (e) {
        productWizardConsole.error(e);
      }
    }

    return result;
  }
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  protected evalCondition(condition: string, productItemAsOneDimensionalObject: any): boolean {
    let result = true;
    if (condition?.length) {
      // Does this condition care about the attr that we're testing?
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      const replacedValues: any = {};
      const conditionedColumnNames = condition.match(this.regex);
      if (conditionedColumnNames) {
        conditionedColumnNames.forEach((conditionColumnNameAsCode) => {
          const columnName = this.extractConditionedColumnName(conditionColumnNameAsCode);
          condition = condition.replace(
            conditionColumnNameAsCode,
            "productItemAsOneDimensionalObject['" + columnName + "']",
          );
          replacedValues['productItemAsOneDimensionalObject[' + columnName + ']'] =
            productItemAsOneDimensionalObject[<string>columnName];
        });
      }

      try {
        this.log('simpleConditionTest:', condition, replacedValues);
        result = eval(condition);
      } catch (e) {
        productWizardConsole.error(e);
      }
    }

    return result;
  }

  protected isNotIn(columnValue: string[] | string, conditionValue: string[] | string): boolean {
    if (!columnValue) {
      return true;
    }

    if (Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
      return columnValue.indexOf(conditionValue) < 0;
    } else if (!Array.isArray(columnValue) && Array.isArray(conditionValue)) {
      return conditionValue.indexOf(columnValue) < 0;
    } else if (Array.isArray(columnValue) && Array.isArray(conditionValue)) {
      const found = conditionValue.filter((value) => {
        return columnValue.indexOf(value) >= 0;
      });

      return found.length == 0;
    } else if (!Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
      return columnValue != conditionValue;
    }

    return false;
  }

  protected isIn(columnValue: string[] | string, conditionValue: string[] | string): boolean {
    if (!columnValue) {
      return false;
    }

    if (Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
      return columnValue.indexOf(conditionValue) >= 0;
    } else if (!Array.isArray(columnValue) && Array.isArray(conditionValue)) {
      return conditionValue.indexOf(columnValue) >= 0;
    } else if (Array.isArray(columnValue) && Array.isArray(conditionValue)) {
      const found = conditionValue.filter((value) => {
        return columnValue.indexOf(value) >= 0;
      });

      return found.length > 0;
    } else if (!Array.isArray(columnValue) && !Array.isArray(conditionValue)) {
      return columnValue == conditionValue;
    }

    return false;
  }

  protected extractConditionedColumnName(condition: string): string | null {
    const conditionedKey = condition.match(this.regex)?.pop();
    if (conditionedKey) {
      return conditionedKey.replace('$', '').replace('{', '').replace('}', '');
    }

    return null;
  }

  /* SET AND GET */
  public getConstraintsFor(attributeName: string, attributeValue: string | number): string | null {
    const attr = this.getAttribute(attributeName);
    if (attr) {
      if (Object.prototype.hasOwnProperty.call(attr.valuesAndConstraints, attributeValue)) {
        return attr.valuesAndConstraints[attributeValue];
      }
    }
    return null;
  }

  public getAttribute(attributeName: string): ProductAttrInterface | null {
    if (Object.prototype.hasOwnProperty.call(this.attributes, attributeName)) {
      return this.attributes[attributeName];
    }

    return null;
  }
}
